import {
  Account,
  ZuoraPaymentMethod,
  ZuoraPaymentMethodTypes,
} from "../../../api/opb/opbTypes";

export function isExpiredCard(
  method: ZuoraPaymentMethod,
  now = new Date()
): boolean {
  return (
    method.type === ZuoraPaymentMethodTypes.CreditCard &&
    (method.expYear < now.getFullYear() ||
      (method.expYear === now.getFullYear() &&
        method.expMonth < now.getMonth() + 1))
  );
}

export interface PaymentSettingsClient {
  fetchAccount: (id: string) => Promise<Account>;
  fetchPaymentMethods: (id: string) => Promise<{ data: ZuoraPaymentMethod[] }>;
  updateUserAccount: (params: {
    accountId: string;
    autoPay?: boolean;
    defaultPaymentMethodId?: string | null;
  }) => Promise<unknown>;
  deletePaymentMethod: (id: string) => Promise<{ success: boolean }>;
}

export async function changeDefaultPaymentMethod(
  client: PaymentSettingsClient,
  accountId: string,
  methodId: string
): Promise<void> {
  const { data: methods } = await client.fetchPaymentMethods(accountId);
  const method = methods.find((value) => value.id === methodId);
  if (method == null) {
    throw new Error("The payment method is no longer available.");
  }
  if (isExpiredCard(method))
    throw new Error(
      "Update the card expiration date before making it the default."
    );
  await client.updateUserAccount({
    accountId,
    defaultPaymentMethodId: methodId,
  });
  const account = await client.fetchAccount(accountId);
  if (account.defaultPaymentMethodId !== methodId) {
    throw new Error("Your default payment method could not be confirmed.");
  }
}

export async function removeSavedPaymentMethod(
  client: PaymentSettingsClient,
  accountId: string,
  methodId: string
): Promise<void> {
  const [account, { data: methods }] = await Promise.all([
    client.fetchAccount(accountId),
    client.fetchPaymentMethods(accountId),
  ]);
  const method = methods.find((value) => value.id === methodId);
  if (method == null) {
    throw new Error("The payment method is no longer available.");
  }
  const isDefault =
    method.default || account.defaultPaymentMethodId === methodId;
  if (methods.length === 1 && account.autopay) {
    throw new Error(
      "Turn off Auto-Pay before removing your only payment method."
    );
  }
  if (isDefault && methods.length > 1) {
    throw new Error(
      "Choose another default payment method before removing this one."
    );
  }
  if (isDefault) {
    await client.updateUserAccount({ accountId, defaultPaymentMethodId: null });
    const [updatedAccount, { data: updatedMethods }] = await Promise.all([
      client.fetchAccount(accountId),
      client.fetchPaymentMethods(accountId),
    ]);
    // A successful PUT alone does not prove Zuora has cleared the default.
    if (
      updatedAccount.autopay ||
      updatedAccount.defaultPaymentMethodId === methodId ||
      updatedMethods.some((value) => value.id === methodId && value.default)
    ) {
      throw new Error(
        "Your default payment method could not be cleared. Please try again."
      );
    }
  }
  const result = await client.deletePaymentMethod(methodId);
  if (result.success !== true) {
    throw new Error("The payment method could not be removed.");
  }
}
